
import React, { useEffect, useRef } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { CustomerContact } from "./types";

interface ContactsTableProps {
  contacts: CustomerContact[];
  selectedContactId: string | null;
  setSelectedContactId: (id: string | null) => void;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  editingContact: Partial<CustomerContact>;
  setEditingContact: (contact: Partial<CustomerContact>) => void;
  handleSaveContact: () => Promise<void>;
  openDeleteDialog: (e: React.MouseEvent, id: number) => void;
  canEdit: boolean;
}

export const ContactsTable = ({
  contacts,
  selectedContactId,
  setSelectedContactId,
  dialogOpen,
  setDialogOpen,
  editingContact,
  setEditingContact,
  handleSaveContact,
  openDeleteDialog,
  canEdit
}: ContactsTableProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  // Auto-scroll to selected contact when selectedContactId changes
  useEffect(() => {
    if (selectedContactId && selectedRowRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const selectedRow = selectedRowRef.current;
      
      const containerRect = container.getBoundingClientRect();
      const rowRect = selectedRow.getBoundingClientRect();
      
      // Check if the selected row is not fully visible
      if (rowRect.top < containerRect.top || rowRect.bottom > containerRect.bottom) {
        // Calculate the scroll position to center the selected row
        const containerScrollTop = container.scrollTop;
        const rowOffsetTop = selectedRow.offsetTop;
        const containerHeight = container.clientHeight;
        const rowHeight = selectedRow.clientHeight;
        
        const scrollTo = rowOffsetTop - (containerHeight / 2) + (rowHeight / 2);
        
        container.scrollTo({
          top: Math.max(0, scrollTo),
          behavior: 'smooth'
        });
      }
    }
  }, [selectedContactId]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Contacts</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              className="border-primary/20 bg-primary hover:bg-primary/90 text-black"
              disabled={!canEdit}
              onClick={() => setEditingContact({})}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white text-black">
            <DialogHeader>
              <DialogTitle>{editingContact.id ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="customer_name">Name</Label>
                <Input
                  id="customer_name"
                  className="bg-white text-black"
                  value={editingContact.customer_name || ''}
                  onChange={(e) => setEditingContact({...editingContact, customer_name: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  className="bg-white text-black"
                  value={editingContact.title || ''}
                  onChange={(e) => setEditingContact({...editingContact, title: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="support_status">Support Status</Label>
                <Select 
                  value={editingContact.support_status || 'Neutral'}
                  onValueChange={(value) => setEditingContact({...editingContact, support_status: value as any})}
                >
                  <SelectTrigger className="bg-white text-black">
                    <SelectValue placeholder="Select support status" className="bg-white text-black"/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Is Supporter">Is Supporter</SelectItem>
                    <SelectItem value="Neutral">Neutral</SelectItem>
                    <SelectItem value="Is Against">Is Against</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="executive_mapped">Executive Mapped</Label>
                <Input
                  id="executive_mapped"
                  className="bg-white text-black"
                  value={editingContact.executive_mapped || ''}
                  onChange={(e) => setEditingContact({...editingContact, executive_mapped: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Input
                  id="status"
                  className="bg-white text-black"
                  value={editingContact.status || ''}
                  onChange={(e) => setEditingContact({...editingContact, status: e.target.value})}
                />
              </div>
              <Button onClick={handleSaveContact} className="w-full">
                {editingContact.id ? 'Update' : 'Add'} Contact
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-white/10 rounded-lg overflow-hidden">
        {/* Sticky Header */}
        <div className="bg-secondary-light/5">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-primary-light text-[9px] sticky top-0 bg-secondary-light/5 w-[15%] z-10">Name</TableHead>
                <TableHead className="text-primary-light text-[9px] sticky top-0 bg-secondary-light/5 w-[25%] z-10">Title</TableHead>
                <TableHead className="text-primary-light text-[9px] sticky top-0 bg-secondary-light/5 w-[10%] z-10">Support</TableHead>
                <TableHead className="text-primary-light text-[9px] sticky top-0 bg-secondary-light/5 w-[15%] z-10">Exec Mapped</TableHead>
                <TableHead className="text-primary-light text-[9px] sticky top-0 bg-secondary-light/5 w-[20%] z-10">Status</TableHead>
                <TableHead className="text-primary-light text-[9px] sticky top-0 bg-secondary-light/5 w-[20%] z-10">Actions</TableHead>
              </TableRow>
            </TableHeader>
          </Table>
        </div>

        {/* Scrollable Body */}
        <div ref={scrollContainerRef} className="max-h-[405px] overflow-y-auto">
          <Table>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow 
                  key={contact.id}
                  ref={selectedContactId === contact.id.toString() ? selectedRowRef : null}
                  className={`cursor-pointer ${selectedContactId === contact.id.toString() ? 'bg-primary/20' : ''}`}
                  onClick={() => setSelectedContactId(
                    selectedContactId === contact.id.toString() ? null : contact.id.toString()
                  )}
                >
                  <TableCell className="text-white text-[9px]">{contact.customer_name}</TableCell>
                  <TableCell className="text-white text-[9px]">{contact.title}</TableCell>
                  <TableCell className="text-white text-[9px]">{contact.support_status}</TableCell>
                  <TableCell className="text-white text-[9px]">{contact.executive_mapped}</TableCell>
                  <TableCell className="text-white text-[9px]">{contact.status}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canEdit) {
                            setEditingContact(contact);
                            setDialogOpen(true);
                          }
                        }}
                        className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                        disabled={!canEdit}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => openDeleteDialog(e, contact.id)}
                        className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                        disabled={!canEdit}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
